import { DIFFICULTY_CONFIG } from '../../../config/difficulty.config';
import { PLAYER_CONFIG } from '../../../config/player.config';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { deriveJumpPhysics } from '../../../physics/JumpPhysics';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLine, entryConnector, laneArea, moduleSides, obstacleRowOn, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type GroundSurface, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_08 – ramp 40 m (design/01_segments/SEG_08_Ramp; the sheet's 30 m standardised to 40 m).
 *
 *   z 4–14    ramp in the center lane, 0 → 1.8 m (walkable slope 0.18)
 *   z 14–32   raised platform 1.8 m – the risk/reward route with bottles
 *   z 32      drop back to the ground (lands around z 35–39), plain ground before OUT
 *   lanes 0/2 ground route with jump / slide obstacles (no lane changes: the platform sides are walls)
 *
 * From a side lane the ramp can be entered only while it is lower than a step (z < 5.7);
 * later the platform side is a wall (stumble). Leaving the platform sideways is a drop.
 */
export const RAMP = {
  length: 40,
  lane: 1,
  ramp: { zFrom: 4, zTo: 14 },
  platform: { zFrom: 14, zTo: 32 },
  height: 1.8,
  rows: { R12: 12, R18: 18, R24: 24, R30: 30 },
} as const;

const SIDE_LANES = [0, WORLD_CONFIG.laneCount - 1] as const;
const SIDE_TYPES = [ObstacleType.Jump, ObstacleType.Slide] as const;
const SPACING = 2;

const surfaces: GroundSurface[] = [
  { id: 'SURF_RAMP', ...laneArea(WORLD_CONFIG, RAMP.lane, RAMP.lane, RAMP.ramp.zFrom, RAMP.ramp.zTo), heightFrom: 0, heightTo: RAMP.height },
  {
    id: 'SURF_PLATFORM',
    ...laneArea(WORLD_CONFIG, RAMP.lane, RAMP.lane, RAMP.platform.zFrom, RAMP.platform.zTo),
    heightFrom: RAMP.height,
    heightTo: RAMP.height,
  },
];

/** Bottle height along the platform route: on the ramp, on the platform, then the falling line after the drop. */
function platformLineHeight(z: number): number {
  const { ramp, platform } = RAMP;
  if (z <= ramp.zFrom) return COLLECTIBLE_FLOAT_HEIGHT;
  if (z <= ramp.zTo) return COLLECTIBLE_FLOAT_HEIGHT + (RAMP.height * (z - ramp.zFrom)) / (ramp.zTo - ramp.zFrom);
  if (z <= platform.zTo) return COLLECTIBLE_FLOAT_HEIGHT + RAMP.height;
  // Free fall from the platform edge at start speed – where a runner who just walks off will be.
  const t = (z - platform.zTo) / DIFFICULTY_CONFIG.speed.start;
  const drop = 0.5 * deriveJumpPhysics(PLAYER_CONFIG).gravity * t * t;
  return COLLECTIBLE_FLOAT_HEIGHT + Math.max(0, RAMP.height - drop);
}

/** Side-lane obstacles alternating between the two ground lanes (`first` gets rows 12 and 24). */
function groundRoute(first: number, second: number): FixedObstaclePlacement[] {
  return [
    { spawnPointId: `SOCKET_OBS_R12_L${first}`, obstacleId: 'OBS_BARRIER_A' },
    { spawnPointId: `SOCKET_OBS_R18_L${second}`, obstacleId: 'OBS_PALLET_A' },
    { spawnPointId: `SOCKET_OBS_R24_L${first}`, obstacleId: 'OBS_SCAFFOLD_PASS_A' },
    { spawnPointId: `SOCKET_OBS_R30_L${second}`, obstacleId: 'OBS_CONE_ROW_A' },
  ];
}

export const SEG_RAMP_40M: SegmentDefinition = {
  id: 'SEG_RAMP_40M',
  kind: SegmentKind.Straight,
  displayName: 'Rampe 40 m',
  length: RAMP.length,
  width: WORLD_CONFIG.trackWidth,
  // Greybox ramp + platform on two base road modules – until the Blender module exists.
  visualAssetId: 'SEG_RAMP_40M',
  extraVisuals: [
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 0 } },
    { assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } },
  ],

  entry: entryConnector(),
  exits: [straightExit(RAMP.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: { surfaces, gaps: [], ceilings: [] },

  obstacleSpawnPoints: Object.entries(RAMP.rows).flatMap(([row, z]) => obstacleRowOn(WORLD_CONFIG, row, z, SIDE_LANES, SIDE_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    { id: 'GROUND_LEFT_FIRST', weight: 1, placements: groundRoute(SIDE_LANES[0], SIDE_LANES[1]) },
    { id: 'GROUND_RIGHT_FIRST', weight: 1, placements: groundRoute(SIDE_LANES[1], SIDE_LANES[0]) },
  ],

  collectibleSpawnPoints: [
    ...collectibleLine(WORLD_CONFIG, 'PLATFORM', [{ lane: RAMP.lane, from: 1, to: RAMP.length - 1, spacing: SPACING, height: platformLineHeight }]),
    ...SIDE_LANES.flatMap((lane) =>
      collectibleLine(WORLD_CONFIG, `GROUND_L${lane}`, [{ lane, from: 1, to: RAMP.length - 1, spacing: SPACING, height: () => COLLECTIBLE_FLOAT_HEIGHT }]),
    ),
  ],

  ...moduleSides(RAMP.length),

  generation: {
    weight: 0.25,
    minDistance: 250,
    minSegmentsAfterJunction: 1,
    family: 'RAMP',
    tags: ['ramp', 'terrain', 'platform'],
  },
};
